"use client";

import { c as _c } from "react/compiler-runtime";
import { FCC_SERVICE_LINE_FILENAME_ABBR, getFccReferralRegion } from "@/configuration/groups/fcc/configuration";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { formatIsoDate } from "@/src/lib/utils/date-and-phone-utils";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Referral, SchedulingPriority } from "@/src/models/documents/referral";
import { getPersonName } from "@/src/models/person";
const FCC_UPLOAD_FOLDER = "External Referrals";
interface FCCReferralInstructionsProps {
  referral: Referral;
}
function buildFccFileName(referral: Referral): string | null {
  const firstInitial = referral.patient?.givenName?.[0]?.toUpperCase();
  const lastName = referral.patient?.familyName ?? null;
  const serviceLineAbbr = referral.serviceLine ? FCC_SERVICE_LINE_FILENAME_ABBR[referral.serviceLine as ServiceLine] : undefined;
  const state = referral.referringLocation?.address?.state ?? null;
  const postalCode = referral.referringLocation?.address?.postalCode ?? null;
  const region = state ? getFccReferralRegion(state, postalCode) : null;
  const regionForName = region && region !== "Other" ? region : null;
  const parts = [firstInitial ? `${firstInitial}.` : null, lastName, serviceLineAbbr, regionForName].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(" ") : null;
}
export function FCCReferralInstructions(t0) {
  const $ = _c(78);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.referringProvider) {
    t1 = referral.referringProvider ? getPersonName(referral.referringProvider) : "";
    $[0] = referral.referringProvider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringProviderName = t1;
  const referringProviderNpi = referral.referringProvider?.npiNumber ?? null;
  const referringLocationName = referral.referringLocation?.name ?? null;
  const referringLocationFax = referral.referringLocation?.faxPhoneNumber ?? null;
  const serviceLine = referral.serviceLine ?? null;
  let t2;
  if ($[2] !== referral.dateOfReferral) {
    t2 = referral.dateOfReferral ? formatIsoDate(referral.dateOfReferral) : null;
    $[2] = referral.dateOfReferral;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dateOfReferral = t2;
  const schedulingPriority = referral.schedulingPriority ?? SchedulingPriority.ROUTINE;
  let t3;
  if ($[4] !== referral) {
    t3 = buildFccFileName(referral);
    $[4] = referral;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const fileName = t3;
  const isVaMilitary = !!referral.section?.reviewedContent?.isVaMilitaryReferral;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="font-medium text-center">FCC Referral Instructions:</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isVaMilitary) {
    t5 = isVaMilitary && <div className="flex items-center justify-center gap-2 rounded-md border border-yellow-400 bg-yellow-50 px-3 py-2 text-yellow-800 dark:border-yellow-600 dark:bg-yellow-950 dark:text-yellow-200"><Badge className="bg-yellow-500 text-white dark:bg-yellow-600">VA / Military / ADSM</Badge><span className="font-medium">This is a VA / Military / ADSM referral. No Clinical Form Necessary.</span></div>;
    $[7] = isVaMilitary;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="text-muted-foreground">Use the values below when entering this referral into the EMR. Click any value to copy.</p>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="font-semibold text-foreground">Referring Provider</span>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="text-muted-foreground">Name: </span>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== referringProviderName) {
    t9 = <div>{t8}{referringProviderName ? <CopyableContent text={referringProviderName} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[12] = referringProviderName;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-muted-foreground">NPI: </span>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== referringProviderNpi) {
    t11 = <div>{t10}{referringProviderNpi ? <CopyableContent text={referringProviderNpi} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[15] = referringProviderNpi;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t9) {
    t12 = <div>{t7}<div className="ml-4 mt-1 flex flex-col gap-1">{t9}{t11}</div></div>;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="font-semibold text-foreground">Referring Location</span>;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="text-muted-foreground">Name: </span>;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== referringLocationName) {
    t15 = <div>{t14}{referringLocationName ? <CopyableContent text={referringLocationName} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[22] = referringLocationName;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="text-muted-foreground">Fax: </span>;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== referringLocationFax) {
    t17 = <div>{t16}{referringLocationFax ? <CopyableContent text={referringLocationFax} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[25] = referringLocationFax;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t15 || $[28] !== t17) {
    t18 = <div>{t13}<div className="ml-4 mt-1 flex flex-col gap-1">{t15}{t17}</div></div>;
    $[27] = t15;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="font-semibold text-foreground">Referral Information</span>;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="text-muted-foreground">Service Line: </span>;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== serviceLine) {
    t21 = <div>{t20}{serviceLine ? <CopyableContent text={serviceLine} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[32] = serviceLine;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="text-muted-foreground">Referral Date: </span>;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== dateOfReferral) {
    t23 = <div>{t22}{dateOfReferral ? <CopyableContent text={dateOfReferral} /> : <span className="text-muted-foreground italic">Not provided</span>}</div>;
    $[35] = dateOfReferral;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="text-muted-foreground">Scheduling Priority: </span>;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== schedulingPriority) {
    t25 = <div>{t24}<CopyableContent text={schedulingPriority} /></div>;
    $[38] = schedulingPriority;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t21 || $[41] !== t23 || $[42] !== t25) {
    t26 = <div>{t19}<div className="ml-4 mt-1 flex flex-col gap-1">{t21}{t23}{t25}</div></div>;
    $[40] = t21;
    $[41] = t23;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <span className="font-semibold text-foreground">Document Upload</span>;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="text-muted-foreground">File name: </span>;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== fileName) {
    t29 = fileName ? <CopyableContent text={fileName} /> : <span className="text-muted-foreground italic">Not enough info</span>;
    $[46] = fileName;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className="text-muted-foreground text-xs ml-2">(First Initial + Last Name + Service Type + Region)</span>;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] !== t29) {
    t31 = <div>{t28}{t29}{t30}</div>;
    $[49] = t29;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <div><span className="text-muted-foreground">Folder: </span><CopyableContent text={FCC_UPLOAD_FOLDER} /></div>;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== t31) {
    t33 = <div>{t27}<div className="ml-4 mt-1 flex flex-col gap-1">{t31}{t32}</div></div>;
    $[52] = t31;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== t12 || $[55] !== t18 || $[56] !== t26 || $[57] !== t33) {
    t34 = <div className="space-y-3">{t12}{t18}{t26}{t33}</div>;
    $[54] = t12;
    $[55] = t18;
    $[56] = t26;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== t34 || $[60] !== t5) {
    t35 = <EhrInstructionsCard><div className="flex flex-col gap-4 text-sm">{t4}{t5}{t6}{t34}</div></EhrInstructionsCard>;
    $[59] = t34;
    $[60] = t5;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== referral.patient) {
    t36 = <PatientCard patient={referral.patient} />;
    $[62] = referral.patient;
    $[63] = t36;
  } else {
    t36 = $[63];
  }
  let t37;
  let t38;
  if ($[64] !== referral) {
    t37 = <ReferralProviderCards referral={referral} />;
    t38 = <ReferralCard referral={referral} isEditable={true} />;
    $[64] = referral;
    $[65] = t37;
    $[66] = t38;
  } else {
    t37 = $[65];
    t38 = $[66];
  }
  let t39;
  if ($[67] !== referral.primaryCoverage) {
    t39 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[67] = referral.primaryCoverage;
    $[68] = t39;
  } else {
    t39 = $[68];
  }
  let t40;
  if ($[69] !== referral.secondaryCoverage) {
    t40 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[69] = referral.secondaryCoverage;
    $[70] = t40;
  } else {
    t40 = $[70];
  }
  let t41;
  if ($[71] !== t35 || $[72] !== t36 || $[73] !== t37 || $[74] !== t38 || $[75] !== t39 || $[76] !== t40) {
    t41 = <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">{t35}{t36}{t37}{t38}{t39}{t40}</div>;
    $[71] = t35;
    $[72] = t36;
    $[73] = t37;
    $[74] = t38;
    $[75] = t39;
    $[76] = t40;
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  return t41;
}